
import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BadgeData, BadgeRendererComponent } from '../badge/badge-render.component';

interface ToolbarAction {
    icon: string;
    command: string;
    value?: string;
    title: string;
}

interface PlaceholderDef {
    key: string;
    label: string;
    type: 'text' | 'image';
}

@Component({
    selector: 'app-html-editor',
    standalone: true,
    imports: [CommonModule, FormsModule, BadgeRendererComponent],
    templateUrl: './html-editor.component.html'
})
export class HtmlEditorComponent implements OnInit, AfterViewInit, OnChanges {

    @Input() template: string = '';
    @Input() badgeWidth: number = 400;
    @Input() badgeHeight: number = 250;

    @Output() templateChange = new EventEmitter<string>();
    @Output() templateSaved = new EventEmitter<string>();

    @ViewChild('editorArea') editorArea!: ElementRef<HTMLDivElement>;

    private initialized = false;


    // Placeholders disponibles pour le badge
    availablePlaceholders: PlaceholderDef[] = [
        { key: 'visitorName', label: 'Nom du visiteur', type: 'text' },
        { key: 'company', label: 'Compagnie', type: 'text' },
        { key: 'date', label: 'Date', type: 'text' },
        { key: 'hostName', label: 'Personne rencontrée', type: 'text' },
        { key: 'siteName', label: 'Nom du site', type: 'text' },
        { key: 'photo', label: 'Photo visiteur', type: 'image' },
        { key: 'logo', label: 'Logo entreprise', type: 'image' },
    ];

    // Preview data (données de test pour l'aperçu)
    previewData: BadgeData = {
        visitorName: 'Jean Dupont',
        company: 'Acme Corp',
        date: new Date().toLocaleDateString(),
        hostName: 'Marie Martin',
        siteName: 'Site Principal',
        photo: '',  // placeholder vide pour la preview
        logo: ''
    };

    toolbarActions: ToolbarAction[] = [
        { icon: 'fas fa-bold', command: 'bold', title: 'Gras' },
        { icon: 'fas fa-italic', command: 'italic', title: 'Italique' },
        { icon: 'fas fa-underline', command: 'underline', title: 'Souligné' },
        { icon: 'fas fa-align-left', command: 'justifyLeft', title: 'Aligner à gauche' },
        { icon: 'fas fa-align-center', command: 'justifyCenter', title: 'Centrer' },
        { icon: 'fas fa-align-right', command: 'justifyRight', title: 'Aligner à droite' },
    ];

    fontSizes = ['8px', '10px', '12px', '14px', '16px', '18px', '24px', '32px'];
    colors = ['#000000', '#FF0000', '#00AA00', '#0000FF', '#FF6600', '#9900CC', '#FFFFFF', '#888888'];

    showPreview = false;
    showSourceCode = false;
    sourceHtml = '';

    ngOnInit(): void {
        this.sourceHtml = this.template;
    }

    ngAfterViewInit(): void {
        this.initialized = true;
        if (this.editorArea?.nativeElement && this.template) {
            this.sourceHtml = this.template;
            this.loadTemplateIntoEditor(this.template);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['template'] && this.initialized) {
            const newVal = changes['template'].currentValue || '';
            // Ne pas écraser si c'est un changement interne (venant de l'éditeur)
            if (newVal !== this.sourceHtml) {
                this.sourceHtml = newVal;
                if (!this.showSourceCode) {
                    this.loadTemplateIntoEditor(newVal);
                }
            }
        }
    }

    // Exécuter une commande de formatage
    execCommand(command: string, value?: string): void {
        document.execCommand(command, false, value);
        this.onEditorInput();
        this.editorArea?.nativeElement.focus();
    }

    // Changer la taille de police
    changeFontSize(size: string): void {
        // execCommand fontSize utilise des valeurs 1-7, on contourne avec du CSS inline
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            if (!range.collapsed) {
                const span = document.createElement('span');
                span.style.fontSize = size;
                range.surroundContents(span);
                this.onEditorInput();
            }
        }
    }

    // Changer la couleur du texte
    changeColor(color: string): void {
        document.execCommand('foreColor', false, color);
        this.onEditorInput();
    }

    // Changer la couleur de fond
    changeBgColor(color: string): void {
        document.execCommand('hiliteColor', false, color);
        this.onEditorInput();
    }

    // Insérer un placeholder
    insertPlaceholder(placeholder: PlaceholderDef): void {
        const tag = placeholder.type === 'image'
            ? `{{img:${placeholder.key}}}`
            : `{{${placeholder.key}}}`;

        if (this.showSourceCode) {
            // En vue source : insérer le tag dans le textarea à la position du curseur
            const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                this.sourceHtml = this.sourceHtml.substring(0, start) + tag + this.sourceHtml.substring(end);
                this.template = this.sourceHtml;
                this.templateChange.emit(this.sourceHtml);
                // Repositionner le curseur après le tag inséré
                setTimeout(() => {
                    textarea.selectionStart = textarea.selectionEnd = start + tag.length;
                    textarea.focus();
                });
            }
            return;
        }

        // En vue WYSIWYG : insérer un badge visuel
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();

            const textNode = document.createTextNode(tag);
            range.insertNode(textNode);

            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        this.onEditorInput();
        this.editorArea?.nativeElement.focus();
    }

    // Quand le contenu de l'éditeur change
    onEditorInput(): void {
        if (this.editorArea?.nativeElement) {
            const html = this.serializeEditorContent(this.editorArea.nativeElement);
            this.template = html;
            this.sourceHtml = html;
            this.templateChange.emit(html);
        }
    }

    // Sérialise le contenu: remplace les spans de placeholder par leurs tags {{...}}
    private serializeEditorContent(el: HTMLElement): string {
        const clone = el.cloneNode(true) as HTMLElement;
        const placeholders = clone.querySelectorAll('[data-placeholder]');
        placeholders.forEach(ph => {
            const tag = ph.getAttribute('data-placeholder') || '';
            const textNode = document.createTextNode(tag);
            ph.parentNode?.replaceChild(textNode, ph);
        });
        return clone.innerHTML;
    }

    // Basculer vue source
    toggleSourceView(): void {
        this.showSourceCode = !this.showSourceCode;
        if (!this.showSourceCode) {
            setTimeout(() => {
                this.template = this.sourceHtml;
                this.loadTemplateIntoEditor(this.sourceHtml);
                this.templateChange.emit(this.sourceHtml);
            });
        }
    }

    // Mettre à jour depuis la vue source
    onSourceChange(): void {
        this.template = this.sourceHtml;
        this.templateChange.emit(this.sourceHtml);
    }

    // Charger un template dans l'éditeur (convertit les {{...}} en badges visuels)
    private loadTemplateIntoEditor(html: string): void {
        if (!this.editorArea?.nativeElement) return;

        let editorHtml = html;

        // // Remplacer {{img:key}} par des badges visuels
        // editorHtml = editorHtml.replace(/\{\{img:(\w+)\}\}/g, (match, key) => {
        //     const ph = this.availablePlaceholders.find(p => p.key === key);
        //     const label = ph ? ph.label : key;
        //     return `<span class="inline-block bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded border border-green-300 mx-0.5 cursor-default" contenteditable="false" data-placeholder="${match}">⟨${label}⟩</span>`;
        // });

        // // Remplacer {{key}} par des badges visuels
        // editorHtml = editorHtml.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        //     const ph = this.availablePlaceholders.find(p => p.key === key);
        //     const label = ph ? ph.label : key;
        //     return `<span class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded border border-blue-300 mx-0.5 cursor-default" contenteditable="false" data-placeholder="${match}">⟨${label}⟩</span>`;
        // });

        this.editorArea.nativeElement.innerHTML = editorHtml;
    }

    togglePreview(): void {
        this.showPreview = !this.showPreview;
    }

    saveTemplate(): void {
        this.templateSaved.emit(this.template);
    }

    // Template par défaut
    loadDefaultTemplate(): void {
        const defaultTemplate = `
      <div style="padding:16px;background:#fff;border:2px solid #1e40af;border-radius:8px;font-family:Arial,sans-serif;">
        <div style="text-align:center;margin-bottom:8px;">
          {{img:logo}}
        </div>
        <div style="text-align:center;font-size:10px;color:#666;text-transform:uppercase;letter-spacing:1px;">
          VISITOR / VISITEUR
        </div>
        <div style="text-align:center;margin:8px 0;">
          <div style="width:80px;height:80px;border-radius:50%;overflow:hidden;margin:0 auto;border:2px solid #1e40af;">
            {{img:photo}}
          </div>
        </div>
        <div style="text-align:center;font-size:18px;font-weight:bold;color:#1e40af;">
          {{visitorName}}
        </div>
        <div style="text-align:center;font-size:12px;color:#444;margin-top:4px;">
          {{company}}
        </div>
        <hr style="border:none;border-top:1px solid #ddd;margin:8px 0;" />
        <div style="display:flex;justify-content:space-between;font-size:10px;color:#888;">
          <span>{{date}}</span>
          <span>Hôte: {{hostName}}</span>
        </div>
      </div>
    `;
        this.template = defaultTemplate.trim();
        this.sourceHtml = this.template;
        this.loadTemplateIntoEditor(this.template);
        this.templateChange.emit(this.template);
    }
}